import {
	ClockType,
	FootballState
} from "@prestosports/prestostats-core/Football";
import DateFormat from "../../../../util/date-format";

// Stored game time is canonical countdown-remaining milliseconds
// everywhere (plays, fragments, drives, the game clock). The ascending
// Clock Type setting is display-only: these helpers convert canonical
// values for rendering and convert typed input back. The mirror
// (limit - x) is its own inverse.

export const isAscendingClock = (gameConfig) => {
	// Clock settings live in the clock config section; legacy configs
	// saved before the section existed keep them in general.
	const clockType =
		gameConfig?.clock?.clockType ?? gameConfig?.general?.clockType;
	return clockType === ClockType.ASC.code;
};

const periodLengthMs = (gameConfig) =>
	(gameConfig?.general?.minutesByPeriod || 0) * 60000;

export const toDisplayMs = (canonicalMs, gameConfig) => {
	let result = canonicalMs;
	if (isAscendingClock(gameConfig)) {
		const limit = periodLengthMs(gameConfig);
		result = Math.max(0, Math.min(limit, limit - canonicalMs));
	}
	return result;
};

export const toCanonicalMs = toDisplayMs;

export const toDisplaySeconds = (canonicalSeconds, gameConfig) => {
	let result = canonicalSeconds;
	if (isAscendingClock(gameConfig)) {
		result = toDisplayMs(canonicalSeconds * 1000, gameConfig) / 1000;
	}
	return result;
};

export const toCanonicalSeconds = toDisplaySeconds;

export const getFormattedTime = (playTime, gameConfig) => {
	return DateFormat.formatMilliseconds(
		toDisplayMs(playTime, gameConfig),
		"mm:ss"
	);
};

export const getPlayNumber = (playIndex) => {
	return `${playIndex - 1}`;
};

export const isKickoffPlay = (play) =>
	(play.fragments || []).some(
		(f) => f.associatedState === FootballState.KickOff
	);

export const isGameControlPlay = (play) =>
	(play.fragments || []).some(
		(f) => f.associatedState === FootballState.GameControl
	);

const TURNOVER_STATES = [
	FootballState.PassIntercepted,
	FootballState.FumbleNotRecovered
];

const SPECIAL_TEAMS_STATES = [
	FootballState.KickOff,
	FootballState.Punt,
	FootballState.FieldGoal
];

export const isTurnoverPlay = (play) => {
	const states = (play.fragments || []).map((f) => f.associatedState);
	const hasTurnover = states.some((s) => TURNOVER_STATES.includes(s));
	const isSpecialTeams = states.some((s) => SPECIAL_TEAMS_STATES.includes(s));

	return hasTurnover && !isSpecialTeams;
};

export const buildDriveInfo = (plays) => {
	const ascending = [...plays].sort((a, b) => a.index - b.index);
	const info = {};
	let pendingStart = false;
	let pendingDriveId = null;

	// Authoritative per-drive time of possession from core (matches the
	// boxscore: gap-closed end time + opening-drive pre-roll). Only set on
	// drive-end plays.
	const totalOf = (play) =>
		play.driveTotalTime == null
			? null
			: DateFormat.formatMilliseconds(Math.max(0, play.driveTotalTime), "mm:ss");

	ascending.forEach((play) => {
		info[play.id] = { showDriveStart: false, driveTotalTime: null };

		// A one-play drive (e.g. an end-of-half kickoff) is both the start and
		// the end of its drive: the badge stays on the play itself rather than
		// being deferred to a later scrimmage play that belongs to a different
		// drive.
		if (play.isDriveStart && play.isDriveEnd) {
			info[play.id].showDriveStart = true;
			info[play.id].driveTotalTime = totalOf(play);
			pendingStart = false;
			pendingDriveId = null;
			return;
		}

		// A kickoff opens a drive, but the Drive Start badge is shown on the
		// drive's first scrimmage play. Defer the badge — scoped to the same
		// drive so a kickoff-only drive does not leak its badge onto the next
		// quarter's start-period play.
		if (play.isDriveStart) {
			pendingStart = true;
			pendingDriveId = play.driveId;
		}

		if (
			pendingStart &&
			play.driveId === pendingDriveId &&
			!isKickoffPlay(play) &&
			!isGameControlPlay(play)
		) {
			info[play.id].showDriveStart = true;
			pendingStart = false;
		}

		if (play.isDriveEnd) {
			info[play.id].driveTotalTime = totalOf(play);
			pendingStart = false;
			pendingDriveId = null;
		}
	});

	return info;
};
